<script setup lang="ts">
    import { computed } from 'vue'

    const props = defineProps<{
        startOffset: number | null
        length: number | null
    }>()

    const emit = defineEmits<{
        (e: 'update:startOffset', value: number | null): void
        (e: 'update:length', value: number | null): void
    }>()

    const startOffsetModel = computed({
        get: () => props.startOffset,
        set: (v: number | null) => emit('update:startOffset', v)
    })

    const lengthModel = computed({
        get: () => props.length,
        set: (v: number | null) => emit('update:length', v)
    })
</script>

<template>
    <fieldset class="border p-3 mt-4">
        <legend class="w-auto px-2 text-primary">
            Time Window
        </legend>

        <small class="form-text text-muted mb-3">
            Limits which months are included in the calculation. This applies independently of schedule-dependent settings.
        </small>

        <div class="form-row">
            <!-- Start offset -->
            <div class="form-group mb-4 col">
                <label for="timeWindowStartOffset" class="form-label">
                    Start offset (months):
                </label>

                <input id="timeWindowStartOffset"
                       v-model.number="startOffsetModel"
                       type="number"
                       min="0"
                       step="1"
                       class="form-control"
                       data-testid="time-window-start-offset"
                       placeholder="Optional..." />

                <small class="form-text text-muted">
                    Optional. Zero-based offset into the metric’s monthly values.
                    <strong>0</strong> starts at the first month,
                    <strong>24</strong> starts at month 25 (year 3).
                </small>
            </div>

            <!-- Length -->
            <div class="form-group mb-4 col">
                <label for="timeWindowLength" class="form-label">
                    Window length (months):
                </label>

                <input id="timeWindowLength"
                       v-model.number="lengthModel"
                       type="number"
                       min="1"
                       step="1"
                       class="form-control"
                       data-testid="time-window-length"
                       placeholder="Optional..." />

                <small class="form-text text-muted">
                    Optional. Number of months to include.
                    Leave blank to include all months from the start offset onward.
                </small>
            </div>
        </div>

        <small class="form-text text-muted">
            <strong>Examples:</strong><br />
            <strong>First 36 months:</strong> Start offset = 0, Length = 36.<br />
            <strong>Year 3 only:</strong> Start offset = 24, Length = 12.<br />
            <strong>From year 4 onward:</strong> Start offset = 36, Length empty.
        </small>
    </fieldset>
</template>